import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  CircularProgress,
  Fab,
  Paper,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/AddRounded";
import SongProposals from "./SongProposals";
import SongActionDialog from "./components/SongActionDialog";
import { Alert } from "react-bootstrap";
import songsAPI from "../../restApi/songsApi";
import { SongProposalsContext } from "../../contexts/SongProposalsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  addButtonLargeScreen: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 2,
  },
  // Mobile keeps its own fixed bottom-tab nav (Navigationbar.jsx) on
  // this page - a plain spacing(2) offset here sits right underneath
  // it (bottom nav is 56px tall and stacks on top via a much higher
  // zIndex), hiding the button entirely. Clearing that bar's height
  // plus a small gutter keeps it floating above the tabs instead.
  addButtonSmallScreen: {
    position: "fixed",
    right: theme.spacing(2),
    bottom: "calc(56px + " + theme.spacing(2) + "px)",
    zIndex: 2,
  },
}));

export default () => {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = React.useState({ loading: true, songs: [] });
  const [error] = React.useState("");
  const [songActionDialog, setSongActionDialog] = React.useState({
    song: null,
    open: false,
    action: null,
  });

  const classes = useStyles();
  const theme = useTheme();
  // Matches the breakpoint Navigationbar.jsx uses to decide whether its
  // fixed bottom tab bar is showing - below "md" it always is on this
  // page (see isMobileSearchPage there), so the Fab must stay in its
  // small-screen (bottom nav-aware) position for that whole range, not
  // just below "sm".
  const largeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { markSeen } = React.useContext(SongProposalsContext);

  const handleOpenActionDialog = ({ song, action }) => {
    setSongActionDialog({ song: song, open: true, action: action });
  };

  const handleCloseDialog = (confirmedAction) => {
    if (confirmedAction) {
      // Send appropriate requests for the action
      if (songActionDialog.action === "add") {
        songsAPI
          .patch(songActionDialog.song.url, { status: "approved" })
          .then((res) => {
            // Remove song from songs
            setState({
              loading: false,
              songs: state.songs.filter((s) => s.url !== res.data.url),
            });
          });
      }
      if (songActionDialog.action === "reject") {
        songsAPI
          .patch(songActionDialog.song.url, { status: "rejected" })
          .then((res) => {
            // Remove song from songs
            setState({
              loading: false,
              songs: state.songs.filter(
                (song) => song.url !== songActionDialog.song.url
              ),
            });
          });
      }
    }

    setSongActionDialog({ song: null, open: false, action: null });
  };

  /* Fetch the songs from the API when component mounts */
  React.useEffect(() => {
    songsAPI
      .get("/api/songs/?status=pending")
      .then((res) => {
        setState({
          songs: res.data,
          loading: false,
        });
      })
      // Without this a failed request (e.g. the API refusing someone with
      // no team) left the spinner up forever.
      .catch(() => setState({ songs: [], loading: false }));
  }, []);

  // Visiting this page (with proposals actually loaded) is what "seen"
  // means for the Proposals tab badge and the app-icon badge - see
  // hooks/useSongProposals.
  React.useEffect(() => {
    if (!state.loading) {
      markSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading]);

  return (
    <Container maxWidth="xl">
      {state.loading ? (
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress color="secondary" />
        </div>
      ) : error ? (
        <Alert
          style={{ backgroundColor: "#424242", color: "rgb(191, 199, 199)" }}
        >
          Error: {error}
        </Alert>
      ) : state.songs.length ? (
        <Box pb={8}>
          <SongProposals
            songs={state.songs}
            handleOpenActionDialog={handleOpenActionDialog}
          />
        </Box>
      ) : (
        <Container maxWidth="sm">
          <Paper>
            <Box p={2}>
              <Typography variant="h5">
                Your team does not have any unapproved proposed songs!
              </Typography>
              <Box mt={2}>
                <Typography>
                  When Team leaders approve songs from the "Song Proposals"
                  page, they will be listed in the catalog.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      )}
      {songActionDialog.open && (
        <SongActionDialog
          song={songActionDialog.song}
          open={songActionDialog.open}
          action={songActionDialog.action}
          handleClose={handleCloseDialog}
        />
      )}
      <Fab
        size="medium"
        variant="extended"
        className={
          largeScreen
            ? classes.addButtonLargeScreen
            : classes.addButtonSmallScreen
        }
        onClick={() => history.push("/songs/new", { from: location })}
      >
        <AddIcon />
        Propose a song
      </Fab>
    </Container>
  );
};
